import type { ReactNode } from "react";
import React from "react";
import { color } from "../../ui/index";
import { styled } from "@linaria/react";
import { H4, Text } from "../Text";
import Button from "../Button";
import ScreenGraph from "../Picto/ScreenGraph";
import t from "../i18n";

type ButtonVariant = "primary" | "secondary" | "tertiary" | "ghost" | "destructive" | "link";

type BaseProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  url?: string;
  picto?: JSX.Element;
  className?: string;
};

type DefaultActionProps = {
  onClick?: (() => void) | undefined;
  buttonLabel?: string | undefined;
  buttonVariant?: ButtonVariant;
  buttonPicto?: JSX.Element;
  disabled?: boolean;
  actionsSlot?: never;
};

type CustomActionProps = {
  actionsSlot: ReactNode;
  onClick?: never;
  buttonLabel?: never;
  buttonVariant?: never;
  buttonPicto?: never;
  disabled?: never;
};

type NoActionProps = {
  onClick?: never;
  buttonLabel?: never;
  buttonVariant?: never;
  buttonPicto?: never;
  disabled?: never;
  actionsSlot?: never;
};

type P = BaseProps & (DefaultActionProps | CustomActionProps | NoActionProps);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 64px 20px;
`;

const Title = styled(H4)`
  color: ${color.carbon900};
  max-width: 640px;
`;

const Subtitle = styled(Text)`
  color: ${color.carbon800};
  max-width: 720px;
  padding-top: 4px;
`;

const ActionButton = styled(Button)`
  margin-top: 16px;
`;

const Actions = styled.div`
  margin-top: 16px;
`;

const FourOFourPicto = styled(ScreenGraph)`
  width: 200px;
  padding: 24px;
`;

const Picto = styled.div`
  padding-bottom: 16px;
  min-width: 150px;
  height: 100%;
  max-width: 335px;
  max-height: 335px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

/**
 * Url is for 404 page, picto is provided by the component and url is used as title.
 *
 * To display a button, please provide an onClick callback and a buttonLabel. Button is disabled if disabled is true, false by default.
 * The callback for the button can be an action to perform or a navigation to a new page with Navigate.
 */

const BlankStateV2 = ({
  title,
  subtitle,
  onClick,
  buttonLabel,
  url,
  picto,
  className,
  disabled = false,
  buttonVariant = "primary",
  buttonPicto,
  actionsSlot,
}: P) => (
  <Wrapper className={className}>
    {url ? (
      <>
        <FourOFourPicto />
        <Title>{t("404.title", url)}</Title>
      </>
    ) : (
      <>
        <Picto>{picto}</Picto>
        <Title>{title}</Title>
      </>
    )}
    {subtitle && <Subtitle>{subtitle}</Subtitle>}
    {actionsSlot && <Actions>{actionsSlot}</Actions>}
    {!actionsSlot && onClick && (
      <ActionButton
        variant={buttonVariant}
        onClick={onClick}
        leftIcon={buttonPicto}
        disabled={disabled}
      >
        {buttonLabel}
      </ActionButton>
    )}
  </Wrapper>
);

export default BlankStateV2;
